import { useState, useEffect, useRef, useCallback } from 'react';

export default function useNotifications(userId, isAdmin = false) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const initialFetchDone = useRef(false);

  // Fetch initial notifications from REST API
  const fetchFromAPI = useCallback(async () => {
    if (!userId || userId === 'anonymous') return;
    try {
      const { default: api } = await import('../lib/api');
      const res = await api.get('/api/leads/notifications', { params: { limit: 50, unread_only: false } });
      if (res.data?.notifications) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unread_count || 0);
      }
      initialFetchDone.current = true;
    } catch {}
  }, [userId]);

  // WebSocket connection for real-time updates
  const connect = useCallback(() => {
    if (!userId || userId === 'anonymous') return;

    const adminParam = isAdmin ? '&is_admin=true' : '';
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const hostname = window.location.hostname;
    const wsUrl = `${protocol}://${hostname}:8000/api/notifications/ws/${userId}?${adminParam}`;

    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      // Fetch initial state first, then WebSocket handles real-time
      if (!initialFetchDone.current) {
        fetchFromAPI();
      }
    };

    ws.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);
        // Skip job events (shouldn't come here anymore but defensive)
        if (notification.type && ['job_queued', 'job_started', 'job_progress', 'job_completed', 'job_failed', 'lead_updated', 'lead_processing_started'].includes(notification.type)) {
          return;
        }
        
        setNotifications(prev => {
          // Dedup by ID
          const exists = notification.id && prev.some(n => n.id === notification.id);
          if (exists) return prev;
          const updated = [notification, ...prev].slice(0, 50);
          // Sync unread count
          const newUnread = updated.filter(n => !n.read).length;
          setUnreadCount(newUnread);
          return updated;
        });
      } catch {}
    };

    ws.onclose = () => {
      setTimeout(connect, 5000);
    };

    ws.onerror = () => {
      // Will trigger onclose
    };

    wsRef.current = ws;
  }, [userId, isAdmin, fetchFromAPI]);

  useEffect(() => {
    fetchFromAPI();
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect, fetchFromAPI]);

  // Admin: poll DB notifications every 60s (reduced from 30s to avoid duplicates)
  useEffect(() => {
    if (!isAdmin) return;
    
    const pollAdminDB = async () => {
      try {
        const { default: api } = await import('../lib/api');
        const res = await api.get('/api/admin/notifications', { params: { limit: 10, unread_only: false } });
        const adminNotifs = (res.data.notifications || []).map(n => ({
          id: `admin_${n.id}`,
          title: n.title,
          description: n.description,
          icon: n.icon || 'Bell',
          time: n.created_at,
          read: n.read === 1 || n.read === true,
          link: n.link,
          type: n.type || 'admin',
        }));
        
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const newOnes = adminNotifs.filter(n => !existingIds.has(n.id));
          if (newOnes.length === 0) return prev;
          const merged = [...newOnes, ...prev].slice(0, 50);
          // Sync unread count
          const newUnread = merged.filter(n => !n.read).length;
          setUnreadCount(newUnread);
          return merged;
        });
      } catch {}
    };
    
    pollAdminDB();
    const interval = setInterval(pollAdminDB, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const markRead = useCallback((notificationId) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notificationId ? {...n, read: true} : n
      );
      const newUnread = updated.filter(n => !n.read).length;
      setUnreadCount(newUnread);
      return updated;
    });
    
    // Send to WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'mark_read', notification_id: notificationId }));
    }
    
    // Also call REST API for DB persistence
    if (notificationId && notificationId.startsWith('admin_')) {
      const dbId = notificationId.replace('admin_', '');
      import('../lib/api').then(api => {
        api.default.post('/api/admin/notifications/mark-read', { notification_id: parseInt(dbId) }).catch(() => {});
      });
    }
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({...n, read: true})));
    setUnreadCount(0);
    
    // Send to WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'mark_all_read' }));
    }
    
    // Call REST API for DB persistence
    import('../lib/api').then(api => {
      api.default.post('/api/admin/notifications/mark-read', { mark_all: true }).catch(() => {});
    });
  }, []);

  return { notifications, unreadCount, markRead, markAllRead };
}
